import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Container from "react-bootstrap/Container";
import Pagination from "react-bootstrap/Pagination";
import toast from "react-hot-toast";
import TopBox from "../../components/TopBox";
import Avatar from "../../components/Avatar";
import Asset from "../../components/Asset";
import appStyles from "../../App.module.css";
import btnStyles from "../../styles/Button.module.css";
import styles from "../../styles/PostsPage.module.css";
import postStyles from "../../styles/Post.module.css";
import paginationStyles from "../../styles/CallsheetReceivedPage.module.css";
import NoResults from "../../assets/no-results.png";
import { axiosInstance, axiosReq } from "../../api/axiosDefaults";
import { CLIENT_PROGRAM_HOSTNAME } from "../../utils/config";
import useHostName from "../../hooks/useHostName";

const DEFAULT_PROFILE_IMAGE =
  "https://res.cloudinary.com/davey/image/upload/v1/media/shot_caller_react/images/default_profile_lxlutc";

function CallsheetReceivedPage() {
  const { id } = useParams();
  const host = useHostName();
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [receivedFilter, setReceivedFilter] = useState("all"); // 'all' | 'received' | 'not_received'
  const [recipients, setRecipients] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pageNavLoading, setPageNavLoading] = useState(false);

  const queryString = window.location.search;
  const searchParams = new URLSearchParams(queryString);
  const episodeTitleParam = searchParams.get("episodeTitle");
  const episodeHeading = episodeTitleParam
    ? `Episode ${episodeTitleParam}`
    : null;

  useEffect(() => {
    if (!id) {
      setHasLoaded(true);
      return;
    }

    const controller = new AbortController();
    let isMounted = true;

    setHasLoaded(false);
    if (
      host !== CLIENT_PROGRAM_HOSTNAME &&
      !localStorage.getItem("accessToken")
    ) {
      setHasLoaded(true);
      return;
    }

    const timer = setTimeout(() => {
      const fetchRecipients = async () => {
        try {
          const params = new URLSearchParams();
          if (query) params.set("search", query);
          if (receivedFilter !== "all") {
            params.set(
              "is_received",
              receivedFilter === "received" ? "true" : "false"
            );
          }
          const qs = params.toString();
          const searchPart = qs ? `?${qs}` : "";
          let data;
          if (host === CLIENT_PROGRAM_HOSTNAME) {
            const res = await axiosReq.get(
              `callsheetsnew/${id}/email-recipients/${searchPart}`,
              { signal: controller.signal }
            );
            if (res?.success === false) {
              throw res.error;
            }
            data = res.data;
          } else {
            const response = await axiosInstance.get(
              `${localStorage.getItem(
                "projectSlug"
              )}/callsheetsnew/${id}/email-recipients/${searchPart}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                },
                withCredentials: true,
                signal: controller.signal,
              }
            );
            if (response?.success === false) {
              throw response.error;
            }
            data = response.data;
          }
          if (isMounted) {
            setRecipients(data.results || []);
            setCount(data.count ?? 0);
            setNextUrl(data.next ?? null);
            setPrevUrl(data.previous ?? null);
          }
        } catch (err) {
          // Ignore cancellation errors (prevents React warning after unmount)
          if (controller.signal.aborted || err?.name === "CanceledError") return;
          console.log(err);
          toast.error("Failed to load email recipients.");
          if (isMounted) {
            setRecipients([]);
            setCount(0);
            setNextUrl(null);
            setPrevUrl(null);
          }
        } finally {
          if (isMounted) setHasLoaded(true);
        }
      };

      fetchRecipients();
    }, 1000);

    return () => {
      isMounted = false;
      controller.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, host, query, receivedFilter]);

  const handleNavigate = async (url) => {
    if (!url || pageNavLoading) return;
    setPageNavLoading(true);
    try {
      let data;
      if (host === CLIENT_PROGRAM_HOSTNAME) {
        const res = await axiosReq.get(url);
        if (res?.success === false) {
          throw res.error;
        }
        data = res.data;
      } else {
        const response = await axiosInstance.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          withCredentials: true,
        });
        if (response?.success === false) {
          throw response.error;
        }
        data = response.data;
      }
      setRecipients(data.results || []);
      setCount(data.count ?? 0);
      setNextUrl(data.next ?? null);
      setPrevUrl(data.previous ?? null);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load page.");
    } finally {
      setPageNavLoading(false);
    }
  };

  const showPagination =
    hasLoaded && (prevUrl != null || nextUrl != null);

  return (
    <div>
      <TopBox
        title="Call Sheet"
        title4={"Received Page"}
        episodeTitle={episodeHeading}
      />
      <Button
        className={`${btnStyles.Button} ${btnStyles.Blue} py-0 mt-1`}
        onClick={() => history.goBack()}
      >
        Back
      </Button>
      <p className={`${styles.Blueback} text-center mt-3`}>
        Click the Back button to return to the Callsheet page.</p>
      <Row className="justify-content-center">
        <Col className="mt-2 text-center" xs={12} md={4}>
          <Form
            className={`${styles.SearchBar} mt-3`}
            onSubmit={(event) => event.preventDefault()}
          >
            <Form.Control
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              type="text"
              className={`${styles.InputBar}`}
              placeholder="Search by recipient name, email or status"
            />
          </Form>
        </Col>
        <Col className="mt-2 text-center" xs={12} md={2}>
          <Form.Group controlId="receivedFilter" className="mt-3">
            <Form.Control
              as="select"
              value={receivedFilter}
              onChange={(event) => setReceivedFilter(event.target.value)}
              className={`${styles.InputBar}`}
              aria-label="Filter by received status"
            >
              <option value="all">All</option>
              <option value="received">Received</option>
              <option value="not_received">Not received</option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3 mt-2 px-2">
        <Col>
          {!hasLoaded ? (
            <Container className={appStyles.Content}>
              <p className="text-center text-muted">Loading…</p>
            </Container>
          ) : null}

          {hasLoaded && recipients.length ? (
            <>
              {recipients.map((item) => (
                <Card
                  key={item.id}
                  className={`mb-0 ${postStyles.PostCard}`}
                >
                  <Card.Body className={`py-0 px-0 ${postStyles.PostTop}`}>
                    <Row className="d-flex align-items-center my-0 py-1">
                      <Col xs={12} sm={1} className="text-center">
                        <span
                          className={`${postStyles.WhiteName} ${paginationStyles.RecipientText} ${paginationStyles.RecipientId}`}
                        >
                          {item.id}
                        </span>
                      </Col>
                      <Col xs={12} sm={1} className="text-center">
                        <Avatar
                          src={item.profile_image || DEFAULT_PROFILE_IMAGE}
                          height={35}
                          text=""
                        />
                      </Col>
                      <Col xs={12} sm={3}>
                        <div
                          className={`${postStyles.Content4} d-flex justify-content-center justify-content-sm-start`}
                        >
                          <p
                            className={`${postStyles.WhiteName} ${paginationStyles.RecipientText} text-center text-sm-left`}
                          >
                            {item.full_name}
                          </p>
                        </div>
                      </Col>
                      <Col xs={12} sm={4}>
                        <div
                          className={`${postStyles.Content4} ${paginationStyles.RecipientEmail} d-flex justify-content-center justify-content-sm-start`}
                        >
                          <p
                            className={`${postStyles.WhiteName} ${paginationStyles.RecipientText} ${paginationStyles.RecipientEmailText} text-center text-sm-left`}
                          >
                            {item.email}
                          </p>
                        </div>
                      </Col>
                      <Col xs={12} sm={3} className="text-center">
                        <span
                          className={`${postStyles.WhiteName} ${paginationStyles.RecipientText}`}
                        >
                          {item.is_received ? "Received" : "Not received"}
                        </span>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
              {showPagination && (
                <div
                  className={`d-flex justify-content-center align-items-center flex-wrap mt-3 ${paginationStyles.paginationRoot}`}
                >
                  {count > 0 && (
                    <span
                      className={`${postStyles.WhiteName} mr-3 mb-2 mb-md-0 ${paginationStyles.RecipientText}`}
                    >
                      {count} total
                    </span>
                  )}
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      onClick={() => handleNavigate(prevUrl)}
                      disabled={prevUrl == null || pageNavLoading}
                    />
                    <Pagination.Next
                      onClick={() => handleNavigate(nextUrl)}
                      disabled={nextUrl == null || pageNavLoading}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : null}

          {hasLoaded && !recipients.length ? (
            <Container className={appStyles.Content}>
              <Asset src={NoResults} message="No Results" />
            </Container>
          ) : null}
        </Col>
      </Row>
    </div>
  );
}

export default CallsheetReceivedPage;
